import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAPIUrl } from "../api/apiUtils";
import { apiFetch } from "../api/apiClient";
import GroupSelector from "../components/GroupSelector";
import ReportSectionChart, { humanizeReportKey } from "./ReportSectionChart";
import "./TableReportsBuilder.scss";

const API_BASE = getAPIUrl();

const REPORTS_ROOT = `${API_BASE}/reports`;
const COMMON_ROOT = `${API_BASE}/common`;
const REPORT_TEMPLATE_STORAGE_KEY = "socio_composite_report_templates";

const TOPIC_OPTIONS = [
  "tgw_healthcare_demography_ecology",
  "tgw_hcs",
  "tgw_economics",
  "tgw_catastrophes",
  "tgw_security_structures",
  "tgw_authority",
  "tgw_positive_assessment",
  "tgw_positive_social",
  "tgw_positive_emotions",
  "tgw_negative_assessment",
  "tgw_negative_social",
  "tgw_negative_emotions",
  "tgw_motivation_activity",
  "tgw_cognitive_communication",
  "tgw_destructive_activity",
  "tgw_affect_lex",
  "tgw_bodily_states_emotions",
  "tgw_invectives",
  "tgw_soft_invectives",
  "tgw_obscene_lex",
  "tgw_youth_jargon",
];

const MARKER_OPTIONS = [
  "trager_coef",
  "logical_coh_coef",
  "sentiment_rate",
  "negation_rate",
  "tgw_positive_assessment",
  "tgw_positive_social",
  "tgw_positive_emotions",
  "tgw_negative_assessment",
  "tgw_negative_social",
  "tgw_negative_emotions",
  "tgw_motivation_activity",
  "tgw_cognitive_communication",
  "tgw_destructive_activity",
  "tgw_affect_lex",
  "tgw_bodily_states_emotions",
  "tgw_invectives",
  "tgw_soft_invectives",
  "tgw_obscene_lex",
  "tgw_youth_jargon",
  "tgw_hcs",
  "tgw_economics",
  "tgw_catastrophes",
  "tgw_security_structures",
  "tgw_authority",
  "be_anger",
  "be_fear",
  "be_sadness",
  "be_disgust",
  "be_shame",
  "be_calm_excitement",
  "be_happyness",
  "be_wonder",
  "ew_positive",
  "ew_negative",
  "ew_ambivalent",
  "ew_de_emotives",
];

const ANOMALITY_OPTIONS = [
  "text_anomality",
  "sentiment_rate",
  "negation_rate",
  "trager_coef",
  "logical_coh_coef",
];

const SECTION_PARAMETER_CONFIG = {
  traeger_coefficients: {
    label: "Период усреднения",
    allLabel: "День",
    mode: "single",
    defaultValue: "day",
    options: ["day", "week", "month"],
  },
  topics_intensity_chart: {
    label: "Тематика",
    allLabel: "Все тематики",
    options: TOPIC_OPTIONS,
  },
  text_markers_chart: {
    label: "Маркеры",
    allLabel: "Все маркеры",
    options: MARKER_OPTIONS,
  },
  text_anomality_chart: {
    label: "Показатель",
    allLabel: "Все показатели",
    options: ANOMALITY_OPTIONS,
  },
};

const PARAMETER_LABELS = {
  day: "День",
  week: "Неделя",
  month: "Месяц",
};

async function fetchJsonAny(urls) {
  let lastErr = null;
  for (const url of urls) {
    try {
      const res = await apiFetch(url);
      if (res.ok) return { res, url };
      lastErr = new Error(`HTTP ${res.status} ${url}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Request failed");
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildAnalysisSpecs(sections, params) {
  return sections.flatMap((section) => {
    const selectedParams = params[section.type] || [];
    const parameterConfig = SECTION_PARAMETER_CONFIG[section.type];
    if (parameterConfig?.mode === "single") {
      return [`${section.type}:${selectedParams[0] || parameterConfig.defaultValue || "day"}`];
    }
    if (parameterConfig && selectedParams.length) {
      return selectedParams.map((parameter) => `${section.type}:${parameter}`);
    }
    return [section.type];
  });
}

function createSectionItem(type) {
  return {
    id: `${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type,
  };
}

function CompositeReportPanel() {
  const [title, setTitle] = useState("Сводный отчёт");
  const [groupRegions, setGroupRegions] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState(() => new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [reportSections, setReportSections] = useState([]);
  const [sectionParams, setSectionParams] = useState({});
  const [availableTopicOptions, setAvailableTopicOptions] = useState([]);
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const [draggingSectionId, setDraggingSectionId] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [sectionsPayload, setSectionsPayload] = useState(null);
  const [pdfExports, setPdfExports] = useState([]);
  const [pdfExportsLoading, setPdfExportsLoading] = useState(false);
  const [pdfFeedback, setPdfFeedback] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REPORT_TEMPLATE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setTemplates(Array.isArray(parsed) ? parsed : []);
    } catch (_) {
      setTemplates([]);
    }
  }, []);

  const persistTemplates = (nextTemplates) => {
    setTemplates(nextTemplates);
    localStorage.setItem(REPORT_TEMPLATE_STORAGE_KEY, JSON.stringify(nextTemplates));
  };

  const topicOptions = useMemo(() => {
    const normalized = availableTopicOptions
      .map((option) => String(option || "").trim())
      .filter(Boolean)
      .map((option) => option.startsWith("tgw/") ? option.slice(4) : option);
    return Array.from(new Set([...TOPIC_OPTIONS, ...normalized]));
  }, [availableTopicOptions]);

  const getParameterConfig = useCallback(
    (type) => {
      const config = SECTION_PARAMETER_CONFIG[type];
      if (type === "topics_intensity_chart") {
        return { ...config, options: topicOptions };
      }
      return config;
    },
    [topicOptions]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTypes(true);
      setError(null);
      try {
        const { res } = await fetchJsonAny([
          `${REPORTS_ROOT}/analysis_types`,
          `${REPORTS_ROOT}/reports/analysis_types`,
        ]);
        const data = await res.json();
        if (cancelled) return;
        const types = data.types || data.analysis_types || [];
        setAnalysisTypes(Array.isArray(types) ? types : []);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const group_ids = Array.from(selectedGroupIds);
    if (!group_ids.length || !startDate || !endDate) {
      setAvailableTopicOptions([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          group_ids: group_ids.join(","),
        });
        const res = await apiFetch(`${REPORTS_ROOT}/topics_intensity_options?${params.toString()}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const options = Array.from(new Set((data.options || []).filter(Boolean)));
        setAvailableTopicOptions(options);
      } catch (_) {
        if (!cancelled) setAvailableTopicOptions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedGroupIds, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingGroups(true);
      try {
        const res = await apiFetch(`${COMMON_ROOT}/groups`);
        if (!res.ok) {
          if (!cancelled) setGroupRegions([]);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setGroupRegions(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setGroupRegions([]);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPdfExports = useCallback(async () => {
    setPdfExportsLoading(true);
    try {
      const res = await apiFetch(`${REPORTS_ROOT}/composite_pdf_exports?limit=100`);
      if (!res.ok) {
        setPdfExports([]);
        return;
      }
      const data = await res.json();
      setPdfExports(Array.isArray(data.data) ? data.data : []);
    } catch (_) {
      setPdfExports([]);
    } finally {
      setPdfExportsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPdfExports();
  }, [loadPdfExports]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => () => stopPoll(), [stopPoll]);

  const fetchReportData = async (reportId) => {
    const { res } = await fetchJsonAny([
      `${REPORTS_ROOT}/reports/${encodeURIComponent(reportId)}/data`,
      `${REPORTS_ROOT}/${encodeURIComponent(reportId)}/data`,
    ]);
    return res.json();
  };

  const fetchReportMeta = async (reportId) => {
    const { res } = await fetchJsonAny([
      `${REPORTS_ROOT}/reports/${encodeURIComponent(reportId)}`,
      `${REPORTS_ROOT}/${encodeURIComponent(reportId)}`,
    ]);
    return res.json();
  };

  const pollReport = useCallback(
    (reportId) => {
      stopPoll();
      setPolling(true);
      const tick = async () => {
        try {
          const meta = await fetchReportMeta(reportId);
          setReportMeta(meta);
          const st = meta.status;
          if (st === "completed" || st === "completed_with_errors") {
            if (meta.error_message) setError(meta.error_message);
            stopPoll();
            const data = await fetchReportData(reportId);
            setSectionsPayload(data);
            loadPdfExports();
          } else if (st === "failed") {
            stopPoll();
            setError(meta.error_message || "Ошибка формирования отчёта");
          }
        } catch (e) {
          setError(e.message || String(e));
          stopPoll();
        }
      };
      tick();
      pollRef.current = setInterval(tick, 2000);
    },
    [stopPoll, loadPdfExports]
  );

  const submitReport = async () => {
    setError(null);
    const group_ids = Array.from(selectedGroupIds);
    const analysis_types = buildAnalysisSpecs(reportSections, sectionParams);
    if (!title.trim()) {
      setError("Укажите заголовок отчёта.");
      return;
    }
    if (!group_ids.length) {
      setError("Выберите хотя бы одно сообщество в списке.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Укажите период: дата начала и дата окончания.");
      return;
    }
    if (!analysis_types.length) {
      setError("Выберите хотя бы один тип анализа / графика.");
      return;
    }

    setSubmitting(true);
    setSectionsPayload(null);
    setReportMeta(null);
    try {
      const body = {
        title: title.trim(),
        group_ids,
        start_date: startDate,
        end_date: endDate,
        analysis_types,
      };
      const res = await apiFetch(`${REPORTS_ROOT}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const created = await res.json();
      const rid = created.id || created.report_id;
      if (!rid) throw new Error("Сервер не вернул id отчёта.");
      setReportMeta(created);
      pollReport(String(rid));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const addReportSection = (type) => {
    if (reportSections.some((section) => section.type === type)) return;
    const item = createSectionItem(type);
    setReportSections((prev) => [...prev, item]);
    const parameterConfig = getParameterConfig(type);
    if (parameterConfig?.mode === "single") {
      setSectionParams((prev) => ({
        ...prev,
        [type]: prev[type]?.length ? prev[type] : [parameterConfig.defaultValue || parameterConfig.options[0]],
      }));
    }
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
  };

  const removeReportSection = (sectionId) => {
    setReportSections((prev) => prev.filter((section) => section.id !== sectionId));
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionId);
      return next;
    });
  };

  const toggleExpandedSection = (sectionId) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const toggleSectionParam = (type, parameter) => {
    const parameterConfig = getParameterConfig(type);
    if (parameterConfig?.mode === "single") {
      setSectionParams((prev) => ({ ...prev, [type]: [parameter] }));
      return;
    }
    setSectionParams((prev) => ({
      ...prev,
      [type]: (prev[type] || []).includes(parameter)
        ? (prev[type] || []).filter((p) => p !== parameter)
        : [...(prev[type] || []), parameter],
    }));
  };

  const clearSectionParams = (type) => {
    const parameterConfig = getParameterConfig(type);
    setSectionParams((prev) => ({
      ...prev,
      [type]: parameterConfig?.mode === "single"
        ? [parameterConfig.defaultValue || parameterConfig.options[0]]
        : [],
    }));
  };

  const moveReportSection = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;
    setReportSections((prev) => {
      const sourceIndex = prev.findIndex((section) => section.id === sourceId);
      const targetIndex = prev.findIndex((section) => section.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const handleGroupsChange = (ids) => {
    setSelectedGroupIds(new Set(ids.map(String)));
  };

  const saveTemplate = () => {
    const name = templateName.trim() || title.trim();
    if (!name || !reportSections.length) {
      setPdfFeedback("Укажите название шаблона и добавьте хотя бы один блок.");
      return;
    }
    const template = {
      id: `${Date.now()}`,
      name,
      title,
      sections: reportSections.map((section) => ({ type: section.type })),
      params: sectionParams,
      createdAt: new Date().toISOString(),
    };
    persistTemplates([template, ...templates.filter((t) => t.name !== name)].slice(0, 30));
    setTemplateName("");
    setPdfFeedback(`Шаблон «${name}» сохранён.`);
  };

  const applyTemplate = (template) => {
    setTitle(template.title || "Сводный отчёт");
    const nextSections = (template.sections || []).map((section) => createSectionItem(section.type));
    setReportSections(nextSections);
    setSectionParams(template.params || {});
    setExpandedSections(new Set(nextSections.map((section) => section.id)));
    setPdfFeedback(`Шаблон «${template.name}» применён.`);
  };

  const removeTemplate = (templateId) => {
    persistTemplates(templates.filter((template) => template.id !== templateId));
  };

  const downloadPdfExport = async (exp) => {
    setPdfFeedback(null);
    try {
      const url = `${REPORTS_ROOT}/composite_pdf_exports/${encodeURIComponent(exp.id)}/download`;
      const res = await apiFetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition");
      let filename = `report_${exp.id.slice(0, 8)}.pdf`;
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
        if (m) filename = decodeURIComponent(m[1].trim());
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      setPdfFeedback(e.message || String(e));
    }
  };

  const sections = sectionsPayload?.sections || null;
  const normalizedGroupRegions = useMemo(
    () =>
      groupRegions.map((block) => ({
        ...block,
        region: block.city || block.region || "Без города",
        groups: (block.groups || []).map((group) => ({
          ...group,
          group_id: String(group.group_id ?? group.id),
        })),
      })),
    [groupRegions]
  );
  const groupLabelMap = useMemo(() => {
    const map = {};
    groupRegions.forEach((block) => {
      (block.groups || []).forEach((group) => {
        const gid = String(group.group_id ?? group.id);
        map[gid] = `${group.title || gid} (ID: ${gid})`;
      });
    });
    return map;
  }, [groupRegions]);

  return (
    <div className="table-reports">
      <h2 className="table-reports__title">Сводный отчёт с графиками</h2>
      <p className="table-reports__meta">
        После отправки отчёт строится на сервере в фоне: можно уйти со страницы — по завершении PDF сохранится в MongoDB,
        придёт уведомление (как у задач парсинга). Группы выбираются из справочника сообществ.
      </p>

      {error && <div className="table-reports__error">{error}</div>}

      <div className="table-reports__toolbar" style={{ alignItems: "stretch" }}>
        <div className="table-reports__field">
          <label htmlFor="cr-title">Заголовок</label>
          <input id="cr-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="table-reports__field">
          <label htmlFor="cr-sd">С даты</label>
          <input id="cr-sd" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="table-reports__field">
          <label htmlFor="cr-ed">По дату</label>
          <input id="cr-ed" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      <section className="table-reports__section">
        <h3>Сообщества</h3>
        {!loadingGroups && !normalizedGroupRegions.length && (
          <div className="table-reports__empty">
            Список групп пуст или недоступен. Проверьте запрос к <code>{COMMON_ROOT}/groups</code> (нужен токен).
          </div>
        )}
        <div className="table-reports__group-picker">
          <GroupSelector
            groups={normalizedGroupRegions}
            selectedIds={Array.from(selectedGroupIds)}
            onChange={handleGroupsChange}
            searchTerm={groupSearch}
            onSearchChange={setGroupSearch}
            selectionType="multiple"
            isLoading={loadingGroups}
            label=""
            placeholder="Город, название или ID сообщества"
          />
        </div>
        {selectedGroupIds.size > 0 && (
          <p className="table-reports__meta">Выбрано сообществ: {selectedGroupIds.size}</p>
        )}
      </section>

      <section className="table-reports__section">
        <h3>Конструктор PDF</h3>
        <div className="table-reports__templates">
          <div className="table-reports__field">
            <label htmlFor="cr-template-name">Название шаблона</label>
            <input
              id="cr-template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Например: недельный мониторинг"
            />
          </div>
          <button type="button" className="table-reports__btn" onClick={saveTemplate}>
            Сохранить шаблон
          </button>
          {templates.length > 0 && (
            <div className="table-reports__template-list">
              {templates.map((template) => (
                <div key={template.id} className="table-reports__template-item">
                  <button type="button" onClick={() => applyTemplate(template)}>
                    {template.name}
                  </button>
                  <button type="button" aria-label="Удалить шаблон" onClick={() => removeTemplate(template.id)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {loadingTypes && <div className="table-reports__meta">Загрузка списка типов…</div>}
        {!loadingTypes && !analysisTypes.length && (
          <div className="table-reports__empty">Типы не получены. Проверьте маршрут /reports/analysis_types на сервере.</div>
        )}
        <div className="table-reports__builder">
          <div className="table-reports__catalog">
            <h4>Доступные блоки</h4>
            {analysisTypes.map((t) => {
              const id = t.id || t.type;
              const name = t.name || t.title || id;
              const alreadyAdded = reportSections.some((section) => section.type === id);
              return (
                <button
                  key={id}
                  type="button"
                  className="table-reports__section-add"
                  disabled={alreadyAdded}
                  onClick={() => addReportSection(id)}
                >
                  <span>{name}</span>
                  <small>{getParameterConfig(id) ? "есть параметры" : "без параметров"}</small>
                </button>
              );
            })}
          </div>

          <div className="table-reports__pdf-plan">
            <h4>Структура PDF</h4>
            {!reportSections.length && (
              <div className="table-reports__empty">Добавьте блоки отчёта из списка слева.</div>
            )}
            {reportSections.map((section, index) => {
              const typeMeta = analysisTypes.find((t) => (t.id || t.type) === section.type);
              const name = typeMeta?.name || typeMeta?.title || humanizeReportKey(section.type);
              const parameterConfig = getParameterConfig(section.type);
              const selectedParams = sectionParams[section.type] || [];
              const isExpanded = expandedSections.has(section.id);
              return (
                <div
                  key={section.id}
                  className={`table-reports__pdf-block${draggingSectionId === section.id ? " table-reports__pdf-block--dragging" : ""}`}
                  draggable
                  onDragStart={() => setDraggingSectionId(section.id)}
                  onDragEnd={() => setDraggingSectionId(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => moveReportSection(draggingSectionId, section.id)}
                >
                  <div className="table-reports__pdf-block-head">
                    <button type="button" className="table-reports__drag-handle" aria-label="Перетащить секцию">
                      ⋮⋮
                    </button>
                    <button type="button" className="table-reports__collapse" onClick={() => toggleExpandedSection(section.id)}>
                      {isExpanded ? "▾" : "▸"}
                    </button>
                    <div>
                      <strong>{index + 1}. {name}</strong>
                      <span>
                        {parameterConfig
                          ? selectedParams.length
                            ? selectedParams.map((p) => PARAMETER_LABELS[p] || humanizeReportKey(p)).join(", ")
                            : parameterConfig.allLabel
                          : "Будет включено в PDF"}
                      </span>
                    </div>
                    <button type="button" className="table-reports__btn table-reports__btn--danger" onClick={() => removeReportSection(section.id)}>
                      Убрать
                    </button>
                  </div>

                  {isExpanded && parameterConfig && (
                    <div className="table-reports__pdf-block-body">
                      <div className="table-reports__param-head">
                        <strong>{parameterConfig.label}</strong>
                        <button type="button" className="table-reports__btn" onClick={() => clearSectionParams(section.type)}>
                          {parameterConfig.allLabel}
                        </button>
                      </div>
                      <div className="table-reports__param-list">
                        {parameterConfig.options.map((option) => (
                          <label key={option} className="table-reports__param-item">
                            <input
                              type={parameterConfig.mode === "single" ? "radio" : "checkbox"}
                              name={`report-section-param-${section.type}`}
                              checked={selectedParams.includes(option)}
                              onChange={() => toggleSectionParam(section.type, option)}
                            />
                            <span>{PARAMETER_LABELS[option] || humanizeReportKey(option)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="table-reports__toolbar">
        <button
          type="button"
          className="table-reports__btn table-reports__btn--primary"
          disabled={submitting || polling}
          onClick={submitReport}
        >
          {submitting ? "Отправка…" : polling ? "Формирование…" : "Сформировать отчёт"}
        </button>
        {reportMeta && (
          <span className="table-reports__meta">
            Статус: <strong>{reportMeta.status}</strong>
            {reportMeta.id ? ` · id ${reportMeta.id}` : ""}
          </span>
        )}
      </div>

      {pdfFeedback && <div className="table-reports__meta" style={{ marginTop: 8 }}>{pdfFeedback}</div>}

      {sections && (
        <section className="table-reports__section">
          <h3>Результат по секциям</h3>
          {Object.entries(sections).map(([analysisType, sectionData]) => (
            <div key={analysisType} style={{ marginBottom: 24 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 16 }}>{humanizeReportKey(analysisType)}</h4>
              <ReportSectionChart analysisType={analysisType} section={sectionData} groupLabelMap={groupLabelMap} />
            </div>
          ))}
        </section>
      )}

      <section className="table-reports__section" style={{ marginTop: 16 }}>
        <h3>Сохранённые PDF</h3>
        <p className="table-reports__meta">
          Файлы хранятся в MongoDB (<code>task_results.composite_report_pdfs</code>, поле <code>pdf</code> — Binary). После
          готовности отчёта PDF создаётся автоматически на сервере.
        </p>
        <div className="table-reports__toolbar" style={{ marginBottom: 8 }}>
          <button type="button" className="table-reports__btn" disabled={pdfExportsLoading} onClick={loadPdfExports}>
            {pdfExportsLoading ? "Обновление…" : "Обновить список"}
          </button>
        </div>
        {!pdfExports.length && !pdfExportsLoading && (
          <div className="table-reports__empty">
            Пока нет PDF в списке. После завершения отчёта запись появится здесь (и в уведомлениях).
          </div>
        )}
        {pdfExports.length > 0 && (
          <div className="table-reports__table-wrap" style={{ maxHeight: 280 }}>
            <table>
              <thead>
                <tr>
                  <th>Заголовок</th>
                  <th>Дата</th>
                  <th>Размер</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pdfExports.map((exp) => (
                  <tr key={exp.id}>
                    <td>{exp.title || "—"}</td>
                    <td>{exp.created_at ? new Date(exp.created_at).toLocaleString() : "—"}</td>
                    <td>{formatFileSize(exp.file_size_bytes)}</td>
                    <td>
                      <button type="button" className="table-reports__btn" onClick={() => downloadPdfExport(exp)}>
                        Скачать
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default CompositeReportPanel;
